// JobClose AI · Slice 2U · Support Search page.
// Server-side contractor search. Requires an explicit query string (min 2
// chars). Never dumps every contractor — the endpoint refuses empty terms.
import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, ShieldCheck } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { supportApi } from "@/lib/supportApi";
import { formatDate } from "@/lib/api";

export default function SupportSearch() {
  const { t } = useLang();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const runSearch = async (e) => {
    e && e.preventDefault && e.preventDefault();
    const term = q.trim();
    if (term.length < 2) {
      setError(t("support.search.error_min_length"));
      setResults([]);
      setCount(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await supportApi.search(term);
      setResults(r.data.results || []);
      setCount(r.data.count || 0);
    } catch (err) {
      setError(err?.response?.data?.detail?.error || t("support.search.error_generic"));
      setResults([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title={t("support.search.title")} subtitle={t("support.search.subtitle")}>
      <section
        data-testid="support-search-header"
        className="jc-card p-4 flex items-center gap-3 mb-6 border-l-4 border-l-[var(--jc-blue)]"
      >
        <ShieldCheck size={20} className="text-[var(--jc-blue)] shrink-0" />
        <div className="flex-1">
          <div className="overline">{t("support.header_kicker")}</div>
          <div className="text-sm text-[var(--jc-text)]">
            {t("support.header_body_read_only")}
          </div>
        </div>
        <div className="text-xs text-[var(--jc-text-soft)]" data-testid="support-viewer">
          {t("support.viewed_by")}: {user?.full_name || user?.email}
        </div>
      </section>

      <form onSubmit={runSearch} className="jc-card p-4 mb-6" data-testid="support-search-form">
        <label className="overline" htmlFor="support-search-input">
          {t("support.search.label")}
        </label>
        <div className="mt-2 flex gap-2">
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)]"
            />
            <input
              id="support-search-input"
              data-testid="support-search-input"
              className="jc-input pl-9 w-full"
              placeholder={t("support.search.placeholder")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
          </div>
          <button
            type="submit"
            data-testid="support-search-submit"
            className="jc-btn-primary px-4"
            disabled={loading}
          >
            {loading ? t("support.search.searching") : t("support.search.submit")}
          </button>
        </div>
        {error && (
          <div className="mt-2 text-xs text-[var(--jc-red)]" data-testid="support-search-error">
            {error}
          </div>
        )}
      </form>

      {results !== null && (
        <div className="jc-card p-4" data-testid="support-search-results">
          <div className="text-sm text-[var(--jc-text-soft)] mb-3">
            {count === 0
              ? t("support.search.results_none")
              : t("support.search.results_count", { count })}
          </div>
          {count > 0 && (
            <table className="w-full text-sm" data-testid="support-search-results-table">
              <thead className="text-[var(--jc-text-soft)] text-xs">
                <tr className="border-b border-[var(--jc-border)]">
                  <th className="text-left py-2">{t("support.search.col_name")}</th>
                  <th className="text-left py-2">{t("support.search.col_company")}</th>
                  <th className="text-left py-2">{t("support.search.col_email")}</th>
                  <th className="text-left py-2">{t("support.search.col_joined")}</th>
                  <th className="text-left py-2">{t("support.search.col_onboarding")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {results.map((row) => (
                  <tr
                    key={row.id}
                    data-testid={`support-search-row-${row.id}`}
                    className="border-b border-[var(--jc-border)] hover:bg-[var(--jc-bg)]"
                  >
                    <td className="py-2">{row.full_name || "—"}</td>
                    <td className="py-2">{row.company_name || "—"}</td>
                    <td className="py-2">{row.email}</td>
                    <td className="py-2">{formatDate(row.created_at)}</td>
                    <td className="py-2">
                      {row.setup_completed === true
                        ? t("support.search.onboarding_complete")
                        : row.setup_completed === false
                        ? t("support.search.onboarding_incomplete")
                        : t("support.unknown")}
                    </td>
                    <td className="py-2 text-right">
                      <Link
                        to={`/app/support/accounts/${row.id}`}
                        data-testid={`support-open-account-btn-${row.id}`}
                        className="inline-flex items-center gap-1 text-xs jc-btn-secondary px-3 py-1.5"
                      >
                        {t("support.search.open_account")} <ArrowRight size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
